import React, { useEffect, useRef, useState } from 'react';

import { defaultIconPosition, defaultIconSize, defaultIconSpacing } from '../../../utils/helper';
import { getIconByName } from '../../../../../reusable/IconLibrary/IconLibrary';

import { getBorderStyle, getDropShadowStyle, getFontFamily, getWidgetOpacity } from './helper';
import { WidgetProps } from './WidgetProps';
import { useWidgetAnimation, EXIT_ANIMATION_HAS_ACTIVE_STATE } from './useWidgetAnimation';

const generateButtonStyle = ({
	isShowWidget,
	isExiting,
	style,
	isHover,
	isClickable,
}: {
	isShowWidget?: boolean;
	isExiting?: boolean;
	style: WidgetProps.StyleProps;
	isHover?: boolean;
	isClickable?: boolean;
}) => {
	const {
		textAlignment,
		textLabel: {
			backgroundColor: textBackgroundColor,
			color,
			font,
			fontSize,
			fontWeight,
			lineHeight,
			shadowColor,
			hoverBackgroundColor: textHoverBackgroundColor,
			hoverColor,
			hoverFontWeight,
		},
		transformSettings: {
			paddingBottom,
			paddingLeft,
			paddingRight,
			paddingTop,
			borderRadiusBottom,
			borderRadiusLeft,
			borderRadiusRight,
			borderRadiusTop,
		},
		backgroundSettings: {
			backgroundColor,
			hoverBackgroundColor,
		},
	} = style;

	// !! Should change to ONLY use backgroundSettings's props after KK-1411 released a few months
	const BGColor =
		backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== '#00000000'
			? backgroundColor
			: textBackgroundColor;

	const hoverBGColor =
		hoverBackgroundColor && hoverBackgroundColor !== 'transparent' && hoverBackgroundColor !== '#00000000'
			? hoverBackgroundColor
			: textHoverBackgroundColor;

	const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);

	return {
		display: isShowWidget || isExiting ? 'flex' : 'none',
		backgroundColor: isHover ? (hoverBGColor || BGColor) : BGColor,
		color: isHover ? (hoverColor || color) : color,
		fontFamily: getFontFamily(font),
		fontSize: `${fontSize}px`,
		fontWeight: isHover ? (hoverFontWeight ?? fontWeight) : fontWeight,
		textAlign: textAlignment || 'center',
		lineHeight: lineHeight ? `${lineHeight}%` : 'normal',
		...(shadowColor && shadowColor !== 'transparent' ? { textShadow: `2px 2px ${shadowColor}` } : {}),
		paddingBottom: `${paddingBottom}px`,
		paddingLeft: `${paddingLeft}px`,
		paddingRight: `${paddingRight}px`,
		paddingTop: `${paddingTop}px`,
		borderTopRightRadius: `${borderRadiusTop}px`,
		borderBottomRightRadius: `${borderRadiusRight}px`,
		borderBottomLeftRadius: `${borderRadiusBottom}px`,
		borderTopLeftRadius: `${borderRadiusLeft}px`,
		width: '100%',
		height: '100%',
		border: isHover ? (hoverBorder || border) : border
	} as React.CSSProperties;
};

const generateIconStyle = ({
	isHover,
	size,
	color,
	hoverColor,
	isClickable,
}: {
	isHover: boolean;
	size: string;
	color: string;
	hoverColor: string;
	isClickable?: boolean;
}) => {
	return {
		fontSize: `${size}px`,
		color: isHover ? (hoverColor || color) : color,
	};
};

const generateWrapperStyle = ({
	style,
	isHover,
	isClickable,
}: {
	style: WidgetProps.StyleProps;
	isHover: boolean;
	isClickable?: boolean;
}) => {
	const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);
	return {
		filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
	} as React.CSSProperties;
};

// The 4 corner placements. Corners stack vertically like top/bottom (icon above or
// below the text), but the icon is aligned to the left/right edge via align-self, so
// icon and text stay separate, non-overlapping flex items with `spacing` as the gap.
const CORNER_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const isCornerPosition = (position: string) => CORNER_POSITIONS.includes(position);
// The icon sits before the text (top row / left) for these, after it otherwise.
const isIconBeforeText = (position: string) =>
	position === 'top' || position === 'left' || position === 'top-left' || position === 'top-right';

const generateIconContainerStyle = ({ position, spacing }: { position: string; spacing: string }) => {
	const [vertical, horizontal] = isCornerPosition(position) ? position.split('-') : [];
	return {
		paddingBottom: position === 'top' || vertical === 'top' ? `${spacing}px` : 0,
		paddingTop: position === 'bottom' || vertical === 'bottom' ? `${spacing}px` : 0,
		paddingLeft: position === 'right' ? `${spacing}px` : 0,
		paddingRight: position === 'left' ? `${spacing}px` : 0,
		...(horizontal ? { alignSelf: horizontal === 'left' ? 'flex-start' : 'flex-end' } : {}),
		lineHeight: 0,
	} as React.CSSProperties;
};

const generateButtonContentStyle = ({ position }: { position: string }) => {
	return {
		display: 'flex',
		// left/right lay out in a row; top/bottom and all corners stack in a column.
		flexDirection: position === 'left' || position === 'right' ? ('row' as any) : 'column',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: '100%',
	} as React.CSSProperties;
};

const ButtonWidget = ({
	id,
	isShowWidget,
	isExiting,
	onExitDone,
	itemRef,
	onClickItem,
	widgetTemplateId,
	style,
	settings,
	isSafari,
	isClickable,
	onContextMenu,
	onUpdateLabel,
}: WidgetProps.BaseProps) => {
	// KK-1182 - Enable text input directly on widget - Solution note
	// Using contentEditable to make span element editable content - better than optimise a text area input
	// But contentEditable & reactjs gonna make the cursor of editing text jump to begin of line. https://github.com/facebook/react/issues/2047
	// Therefore using initialTextValue, dangerouslySetInnerHTML to work it out.

	const {
		textLabel: { text, color, hoverColor },
		iconConfig: { position = defaultIconPosition, size = defaultIconSize, spacing = defaultIconSpacing } = {},
		transformSettings: { exitAnimation = '' } = {},
	} = style;

	const [isHover, setIsHover] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [initialTextValue, setInitialTextValue] = useState(text);

	const textElRef = useRef<HTMLSpanElement | null>();
	const iconElementRef = useRef<HTMLDivElement | null>();
	const bgElementRef = useRef<HTMLDivElement | null>();
	const gElementRef = useRef<SVGGElement | null>();
	// A double-click enters text-edit mode. Only pill/square have a press ("active")
	// animation on click; defer it so a double-click can cancel it before it plays -
	// otherwise double-clicking to edit fires the active animation AND, because the two
	// clicks' colour-flashes overlap, leaves the button stuck on the active colour.
	const clickTimerRef = useRef<ReturnType<typeof setTimeout>>();
	// Only pill/square WITH a linked event have a press to defer/play (see cursor rule).
	const hasActiveState = EXIT_ANIMATION_HAS_ACTIVE_STATE.includes(exitAnimation) && !!isClickable;

	const handleMouseEnter = () => {
		setIsHover(true);
	};

	const handleMouseLeave = () => {
		setIsHover(false);
	};

	const { iconKey } = settings;
	const icon =
		iconKey &&
		getIconByName(iconKey, {
			style: generateIconStyle({ isHover, size, color, hoverColor: hoverColor || color, isClickable }),
		});

	useEffect(() => {
		if (editMode) {
			textElRef.current?.focus();
			return;
		}
	}, [textElRef, editMode]);

	useEffect(() => {
		if (!editMode) {
			setInitialTextValue(text);
		}
	}, [text, editMode]);

	useEffect(() => () => clearTimeout(clickTimerRef.current), []);

	const { triggerClickAnimation } = useWidgetAnimation({ gElementRef, textElementRef: textElRef, iconElementRef, bgElementRef, style, isShowWidget, isExiting, onExitDone, iconSlideFrom: position === 'top-right' || position === 'bottom-right' ? 'right' : 'left', isClickable, isEditing: editMode });

	//If safari then we not add style backface-visibility. This style using to reduce ghost layer appear when drag widget KK-1087
	//But on safari using backface-visibility + foreignObject (inside g tag) will break D&D on safari.
	// More info: https://github.com/bkrem/react-d3-tree/issues/284
	return (
		<g id={widgetTemplateId} className={`${!isSafari ? 'widget' : ''} widget_${widgetTemplateId}`} style={{ opacity: getWidgetOpacity(style.backgroundSettings, isHover) }}>
			<g ref={itemRef}>
				<g
					ref={r => gElementRef.current = r}
					data-template-id={widgetTemplateId}
					data-widget-id={id}
					onClick={(e) => {
						e.stopPropagation();
						if (!isShowWidget || editMode) { return; }
						// No press animation (non pill/square): run immediately as before.
						if (!hasActiveState) {
							triggerClickAnimation(() => onClickItem?.(itemRef));
							return;
						}
						// pill/square: defer the press so onDoubleClick (edit) can cancel it.
						clearTimeout(clickTimerRef.current);
						clickTimerRef.current = setTimeout(() => {
							triggerClickAnimation(() => onClickItem?.(itemRef));
						}, 220);
					}}
					onContextMenu={(e) => {
						e.preventDefault();
						onContextMenu?.(itemRef, !!isShowWidget);
						return false;
					}}
					onDoubleClick={() => {
						// cancel the pending single-click press so it never plays / sticks
						clearTimeout(clickTimerRef.current);
						setEditMode(true);
					}}
					style={generateWrapperStyle({ style, isHover, isClickable })}
				>
					<foreignObject>
						<div
							ref={(r) => (bgElementRef.current = r)}
							className="button-widget"
							style={generateButtonStyle({ isShowWidget, isExiting, style, isHover, isClickable })}
							onMouseEnter={handleMouseEnter}
							onMouseLeave={handleMouseLeave}
						>
							<div style={generateButtonContentStyle({ position })}>
								{iconKey && isIconBeforeText(position) && (
									<div ref={(r) => (iconElementRef.current = r)} style={generateIconContainerStyle({ spacing, position })}>{icon}</div>
								)}

								<span
									ref={(r) => (textElRef.current = r)}
									contentEditable={editMode}
									onInput={(e) => {
										// send text to update
										onUpdateLabel?.(e.currentTarget.innerHTML);
									}}
									onBlur={() => {
										setEditMode(false);
									}}
									dangerouslySetInnerHTML={{ __html: initialTextValue }}
								/>

								{iconKey && !isIconBeforeText(position) && (
									<div ref={(r) => (iconElementRef.current = r)} style={generateIconContainerStyle({ spacing, position })}>{icon}</div>
								)}
							</div>
						</div>
					</foreignObject>
				</g>
			</g>
		</g>
	);
};

export default ButtonWidget;
